import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Crown, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface ValueActivatorProps {
  listingId: string;
  isPremium: boolean;
  onDismiss: () => void;
}

export const ValueActivator = ({ listingId, isPremium, onDismiss }: ValueActivatorProps) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [stats, setStats] = useState({
    contactClicks: 0,
    shares: 0,
    views: 0
  });

  useEffect(() => {
    const checkFirstInteraction = async () => {
      // Get contact clicks
      const { count: contactCount } = await supabase
        .from('listing_contact_clicks')
        .select('*', { count: 'exact', head: true })
        .eq('listing_id', listingId);

      // Get shares
      const { count: shareCount } = await supabase
        .from('listing_social_shares')
        .select('*', { count: 'exact', head: true })
        .eq('listing_id', listingId);

      // Get listing to check views
      const { data: listing } = await supabase
        .from('listings')
        .select('views')
        .eq('id', listingId)
        .maybeSingle();

      const totalContacts = (contactCount || 0);
      const totalShares = (shareCount || 0);
      const totalViews = (listing?.views || 0);

      setStats({
        contactClicks: totalContacts,
        shares: totalShares,
        views: totalViews
      });

      // Check if dismissed before
      const dismissedKey = `value-activator-dismissed-${listingId}`;
      const isDismissed = localStorage.getItem(dismissedKey);

      // Show if:
      // 1. Not premium
      // 2. Has higher engagement threshold (views > 50 OR contacts > 3 OR shares > 2)
      // 3. Not dismissed before
      const hasEngagement = totalViews > 50 || totalContacts > 3 || totalShares > 2;
      
      if (!isPremium && hasEngagement && !isDismissed) {
        setShow(true);
      }
    };

    checkFirstInteraction();
  }, [listingId, isPremium]);

  const handleDismiss = () => {
    localStorage.setItem(`value-activator-dismissed-${listingId}`, 'true');
    setShow(false);
    onDismiss();
  };

  if (!show) {
    return null;
  }

  return (
    <Card className="border-primary bg-gradient-to-br from-primary/10 to-secondary/10 shadow-lg">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-full">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-bold text-lg">¡Tus Anuncios Están Funcionando! 🎉</h3>
              <p className="text-sm text-muted-foreground">
                Tu perfil ya está generando interés
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.views}</div>
            <div className="text-xs text-muted-foreground">Vistas</div>
          </div>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.contactClicks}</div>
            <div className="text-xs text-muted-foreground">Contactos</div>
          </div>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.shares}</div>
            <div className="text-xs text-muted-foreground">Compartidos</div>
          </div>
        </div>

        <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-sm mb-2">
                Con Premium, Puedes Multiplicar Tus Contactos
              </h4>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Mayor visibilidad:</strong> Aparece en los primeros resultados</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Estadísticas completas:</strong> Sigue cada contacto en detalle</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Página premium:</strong> Diseño profesional que convierte más</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Cupones destacados:</strong> Atrae más clientes con ofertas</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Eventos ilimitados:</strong> Crea y publica eventos gratis sin límites</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => navigate("/premium-upgrade")}
            className="w-full bg-gradient-primary hover:opacity-90"
          >
            <Crown className="w-4 h-4 mr-2" />
            Actualizar a Premium Ahora
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="w-full"
            size="sm"
          >
            Recordarme más tarde
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-3">
          🚀 Los perfiles Premium reciben <strong className="text-primary">5x más contactos</strong>
        </p>
      </CardContent>
    </Card>
  );
};
